import { useEffect, useRef } from "react";
import { Outlet, ScrollRestoration, useLocation, useMatches } from "react-router";
import { IdleSessionMonitor } from "../../auth/IdleSessionMonitor";
import { documentTitle } from "../paths";
import type { RouteHandle } from "../paths";

function isRouteHandle(value: unknown): value is RouteHandle {
  return (
    typeof value === "object" && value !== null && typeof (value as RouteHandle).title === "string"
  );
}

/**
 * Root route element (WEB-004). Owns cross-page accessibility behaviour:
 *  - document title from the deepest route handle (WCAG 2.4.2)
 *  - on client-side navigation, focus moves to the new page's h1 so screen readers
 *    announce it (initial page load keeps the browser's default focus)
 */
export function RootLayout() {
  const matches = useMatches();
  const { pathname } = useLocation();
  const firstRender = useRef(true);

  const title = [...matches]
    .reverse()
    .map((m) => m.handle)
    .find(isRouteHandle)?.title;

  useEffect(() => {
    document.title = documentTitle(title);
  }, [title]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const heading = document.querySelector("h1");
    if (heading instanceof HTMLElement) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  }, [pathname]);

  return (
    <>
      <Outlet />
      <ScrollRestoration />
      <IdleSessionMonitor />
    </>
  );
}
